// /launch — Product Hunt launch page with first-24-hours discount code.
// The discount code is validated by Stripe promo codes (Nicole creates PHUNT50 in Stripe dashboard).
// Copy is honest: acknowledges pre-launch state until Nicole flips PH_LAUNCHED to true.
import { useEffect, useMemo, useState } from "react";
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Rocket, Copy, Check, Bell, Clock, Sparkles, Zap, Gift, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { submitLaunchNotify } from "@/lib/api";

// -- CONFIG (edit these when you launch) --------------------------------------
// Flip to true the morning of launch. Change the URL to your live Product Hunt post.
const PH_LAUNCHED = false;
const PH_URL = "https://www.producthunt.com/products/lectura";
const PROMO_CODE = "PHUNT50";                 // 50% off first month — create matching Stripe promo code
const PROMO_LABEL = "50% off first month";
const LAUNCH_DATE_ISO = "2026-11-04T08:00:00-08:00"; // 08:00 PT Product Hunt launch time — edit before launch
// -----------------------------------------------------------------------------

const useCountdown = (targetIso) => {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, over: diff === 0 };
};

export default function LaunchPage() {
  const [email, setEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const [copied, setCopied] = useState(false);
  const { d, h, m, s, over } = useCountdown(LAUNCH_DATE_ISO);

  const copyPromo = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success("Promo code copied — paste it at checkout");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const notify = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    try {
      await submitLaunchNotify(email);
      setNotified(true);
      toast.success("You're on the list — we'll email you the morning we launch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up — try again in a moment.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={PH_LAUNCHED ? "Lectura is Live on Product Hunt — 50% Off First Month" : "Lectura Product Hunt Launch — Notify Me"}
        description={PH_LAUNCHED
          ? "Lectura is live on Product Hunt today. Upvote us + get 50% off your first month of Pro with code PHUNT50."
          : "Lectura is launching on Product Hunt. Get notified and grab an exclusive first-24-hours discount on Pro."}
        canonical="https://getlectura.app/launch"
      />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Rocket className="h-3.5 w-3.5" /> {PH_LAUNCHED ? "We're live on Product Hunt" : "Product Hunt launch"}
            </div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              {PH_LAUNCHED
                ? "Lectura is live. Grab 50% off Pro today only."
                : "Lectura launches on Product Hunt soon. Be there first."}
            </h1>
            <p className="mt-5 text-muted-foreground max-w-2xl mx-auto">
              A full narrated lecture, slide deck, printable study guide, and graded quiz — from any textbook chapter, in under 2 minutes.
              Powered by Gemini 3.1 Pro. Built by one student who was tired of stitching AI tools together.
            </p>
          </div>
        </section>

        {/* Countdown OR live banner */}
        {!PH_LAUNCHED && !over && (
          <section className="border-b border-border/60 bg-secondary/40">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-accent">Launching in</p>
              <div className="mt-4 grid grid-cols-4 gap-3 max-w-md mx-auto" data-testid="launch-countdown">
                {[
                  { v: d, l: "days" }, { v: h, l: "hrs" }, { v: m, l: "min" }, { v: s, l: "sec" },
                ].map((u, i) => (
                  <div key={i} className="rounded-2xl border border-border bg-background p-4">
                    <p className="font-serif text-3xl sm:text-4xl font-bold tabular-nums">{String(u.v).padStart(2, "0")}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{u.l}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-muted-foreground inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {new Date(LAUNCH_DATE_ISO).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
              </p>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-5xl px-4 sm:px-6 py-14 grid gap-8 lg:grid-cols-2 items-start">
          {/* Promo code card */}
          <div className="rounded-3xl border-2 border-accent/40 bg-accent/5 p-6 sm:p-8">
            <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground">
              <Gift className="h-6 w-6" />
            </span>
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-accent">Launch-day promo</p>
            <h2 className="mt-1 font-serif text-3xl font-bold">{PROMO_LABEL}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              First 24 hours from launch. Applies to Pro Monthly and Pro Yearly. New customers only.
            </p>

            <div className="mt-5 flex items-stretch gap-2">
              <div
                data-testid="launch-promo-code"
                className={`flex-1 rounded-xl border-2 border-dashed border-accent/60 bg-background px-4 py-3 text-center font-mono text-2xl font-black tracking-widest select-all ${PH_LAUNCHED ? "" : "opacity-60"}`}
              >
                {PROMO_CODE}
              </div>
              <button
                data-testid="launch-copy-promo"
                onClick={copyPromo}
                disabled={!PH_LAUNCHED}
                className="rounded-xl bg-primary text-primary-foreground px-4 font-bold hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                title={PH_LAUNCHED ? "Copy the promo code" : "Unlocks the moment we launch"}
              >
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
              </button>
            </div>

            {!PH_LAUNCHED && (
              <p className="mt-3 text-xs text-muted-foreground">
                The code unlocks the morning we go live on Product Hunt. Notify me on the right and we'll email it to you the moment it activates.
              </p>
            )}

            <Link
              to="/pricing"
              data-testid="launch-view-pricing"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-border px-5 py-2.5 text-sm font-bold hover:bg-secondary/60 transition"
            >
              See Pro pricing <Zap className="h-4 w-4" />
            </Link>
          </div>

          {/* Notify me OR upvote CTA */}
          <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            {PH_LAUNCHED ? (
              <>
                <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-primary text-primary-foreground">
                  <Rocket className="h-6 w-6" />
                </span>
                <h2 className="mt-4 font-serif text-2xl font-bold">Give us your upvote.</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Every upvote on launch day pushes Lectura higher on Product Hunt — which means more teachers and students find us for the first time.
                </p>
                <a
                  href={PH_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="launch-upvote-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-black hover:opacity-90"
                >
                  <ExternalLink className="h-4 w-4" /> Upvote on Product Hunt
                </a>
                <p className="mt-4 text-xs text-muted-foreground">
                  Then come back and use code <span className="font-mono font-bold">{PROMO_CODE}</span> at checkout.
                </p>
              </>
            ) : notified ? (
              <div className="text-center py-4">
                <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700">
                  <Check className="h-7 w-7" />
                </span>
                <h2 className="mt-4 font-serif text-2xl font-bold">You're on the list.</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  We'll email you the promo code and Product Hunt link the moment we go live.
                </p>
              </div>
            ) : (
              <form onSubmit={notify} data-testid="launch-notify-form">
                <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-primary text-primary-foreground">
                  <Bell className="h-6 w-6" />
                </span>
                <h2 className="mt-4 font-serif text-2xl font-bold">Notify me on launch day.</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  We'll email you the exact link the moment we go live, plus your discount code — one email, no spam.
                </p>
                <label htmlFor="ln-email" className="mt-5 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</label>
                <input
                  id="ln-email" data-testid="launch-notify-email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                  placeholder="you@school.edu"
                />
                <button
                  data-testid="launch-notify-submit"
                  type="submit"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-black hover:opacity-90 transition"
                >
                  <Sparkles className="h-4 w-4" /> Notify me
                </button>
              </form>
            )}
          </div>
        </section>

        {/* What's new — makes the launch page actually useful */}
        <section className="border-t border-border/60 bg-secondary/30">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14">
            <p className="text-xs font-bold uppercase tracking-widest text-accent text-center">What's new in Lectura</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold text-center">What we're launching with.</h2>
            <div className="mt-10 grid gap-4 md:grid-cols-2">
              {[
                { t: "Gemini 3.1 Pro pipeline", b: "Full lecture + slide deck + printable guide + graded quiz from a single textbook chapter, in under 2 minutes." },
                { t: "AI voice narration", b: "Natural narration on every lecture. Play, pause, and adjust pace like a podcast." },
                { t: "Live Class Game Mode", b: "Turn any quiz into a live classroom game with avatars and a real-time leaderboard." },
                { t: "Standards-aligned outputs", b: "Every question tagged to Common Core, NGSS, or TEKS. Free for K-12 educators." },
                { t: "Founding-teacher program", b: "10 teachers get free Pro forever. Apply at /teachers-wanted." },
                { t: "FERPA-Ready + COPPA-compliant", b: "No student data training, ever. DPA available on request for districts." },
              ].map((r, i) => (
                <div key={i} className="rounded-2xl border border-border/60 bg-background p-5">
                  <h3 className="font-bold">{r.t}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{r.b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border/60 bg-primary text-primary-foreground">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <h2 className="font-serif text-3xl font-bold">Try Lectura free while you wait.</h2>
            <p className="mt-3 text-primary-foreground/80 max-w-xl mx-auto">
              Three free lectures — no credit card. Your account carries over, so the promo code applies the day you upgrade.
            </p>
            <Link
              to="/register"
              data-testid="launch-try-free"
              className="mt-6 inline-flex rounded-full bg-accent text-accent-foreground px-6 py-3 font-black"
            >
              Start free
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
